/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
export enum LogLevel {
  OFF = 0,
  DEBUG = 100,
  INFO = 200,
  PERF = 300,
  WARN = 400,
  ERROR = 500
}

export class LogEntry {
  private static httpClient: HttpClient;
  private static username = '';
  private static endPoint: string;

  private static queue: any[] = [];

  static setHttpClient(httpClient) {
    LogEntry.httpClient = httpClient;
  }

  static setEndPoint(endPoint) {
    LogEntry.endPoint = endPoint;
  }

  static setUsername(userName) {
    LogEntry.username = userName;
  }

  static log(source: string, func: (...args: any[]) => void, level: LogLevel, objects: any[]) {
    if (level >= (window[`appConfig`] && window[`appConfig`].logLevel)) {
      const logType = LogEntry.getLogType(level);
      const log = [`[${logType.toUpperCase()}]`, source ? `[${source}] ` : '[] '].concat(
        LogEntry.convertObjToStr(objects)
      );
      func.apply(console, log);

      if (LogEntry.shouldLogToServer(level)) {
        LogEntry.printLogToServer({ message: log.join(' '), logType });
      }
    }
  }

  static abortLogs() {
    LogEntry.serverCall = undefined;
  }

  private static serverCall: any = undefined;

  private static printLogToServer(log?: { message: string; logType: string }) {
    // if there is a log to send, add to the queue
    log && LogEntry.queue.push(log);

    // if a server call is running, then stop.
    if (LogEntry.serverCall) return;

    // get next log from the queue
    log = LogEntry.queue.shift();

    // if list is empty, log is undefined. no log to send.
    if (!log) return;

    // send log to backend.
    LogEntry.serverCall = LogEntry.httpClient
      .post(
        LogEntry.endPoint,
        {
          logMessage: log.message,
          logType: log.logType,
          loginUser: LogEntry.username
        },
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=UTF-8' })
        }
      )
      .toPromise()
      .finally(() => {
        // server call returned. no server call in progress.
        LogEntry.serverCall = undefined;

        // process next log from the queue
        LogEntry.printLogToServer();
      });
  }

  private static shouldLogToServer(logLevel: number): boolean {
    return window[`appConfig`].logToServer || logLevel >= LogLevel.ERROR;
  }

  private static getLogType(level: number): string {
    if (level === LogLevel.WARN) {
      return 'warn';
    }
    if (level === LogLevel.DEBUG) {
      return 'debug';
    }
    if (level === LogLevel.ERROR) {
      return 'error';
    }

    return 'info';
  }

  private static convertObjToStr(val: any) {
    if (val === null) {
      return val;
    }

    if (Array.isArray(val)) {
      return val.map(item => (typeof item === 'object' ? JSON.stringify(item) : item));
    } else if (typeof val === 'object') {
      return JSON.stringify(val);
    } else {
      return val;
    }
  }
}
